import {z} from 'zod'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {useEffect, useState} from 'react'
import {captureException} from '@sentry/react'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {QUERY_KEYS} from '@/queryClient.ts'
import {PaginationResponse, ResponseType} from '@/types.ts'
import {httpGetAssignments} from '@features/home/http/home.http.ts'
import {Assignment} from '@features/assignment/types.ts'
import {AssignmentsURLParams} from '@features/home/types.ts'
import {TABLE_ELEMENT_LIMIT} from '@features/home/utils.tsx'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams.ts'
import {OrderDirection} from '@components/ui/table-atoms'

const ResponseShape = z.object({data: z.array(Assignment), ...PaginationResponse.shape})
type ResponseShape = z.infer<typeof ResponseShape>

export const useHomeAssignments = (
    params: AssignmentsURLParams,
    options?: Omit<UseQueryOptions<ResponseShape>, 'queryKey' | 'queryFn'>
) => {
    const {searchParams, setSearchParams} = useParsedSearchParams(AssignmentsURLParams)
    const [persistedPagination, setPersistedPagination] = useState<PaginationResponse>({
        total: 0,
        last_page: 1,
        per_page: '15',
        current_page: 1
    })

    const query = useQuery({
        queryKey: [QUERY_KEYS.ASSIGNMENTS, QUERY_KEYS.HOME_TABLE, params],
        queryFn: () =>
            parseAxiosPromise({
                axiosPromise: httpGetAssignments({
                    params: {...params, response_type: ResponseType.Enum.extended, limit: TABLE_ELEMENT_LIMIT}
                }),
                onZodError: captureException,
                responseShape: ResponseShape
            }),
        ...options
    })

    useEffect(() => {
        if (query.isSuccess) {
            setPersistedPagination({
                total: query.data.total,
                per_page: query.data.per_page,
                current_page: query.data.current_page,
                last_page: query.data.last_page
            })
        }
    }, [query.data?.data.length])

    return {
        ...query,
        persistedPagination,
        sort: {
            sortName: searchParams.order_by || null,
            direction: searchParams.order_direction || null,
            onSort: (sortName: AssignmentsURLParams['order_by'] | null, direction: OrderDirection | null) =>
                setSearchParams({order_by: sortName || undefined, order_direction: direction || undefined})
        }
    }
}
